import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Image,
  Stack,
  Group,
  Badge,
  Paper,
  Button,
  Grid,
  Alert,
} from '@mantine/core';
import { Helmet } from 'react-helmet-async';
import { Loader } from '../../components/common/Loader';
import {
  IconCalendar,
  IconUser,
  IconClock,
  IconArrowLeft,
  IconShare,
} from '@tabler/icons-react';
import { galleryService } from '../../services/galleryService';

const API_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://thalvora-exports.onrender.com';

const PLACEHOLDER_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='630' viewBox='0 0 1200 630'%3E%3Crect width='1200' height='630' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E";

interface BlogPost {
  title: string;
  content: string;
  image: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  tags: string[];
  slug: string;
}

// 🔥 Fallback static post
const staticPost: BlogPost = {
  title:
    'The Rising Demand for Indian Agricultural Products in Global Markets',

  content: `<p>The global demand for Indian agricultural products has been on a steady rise...</p>`,

  image: '/assets/images/blog/agricultural-exports.jpg',

  author: 'John Doe',

  date: '2024-01-15',

  category: 'Market Insights',

  readTime: '5 min read',

  tags: ['agriculture', 'exports', 'market trends'],

  slug: 'rising-demand-indian-agricultural-products',
};

export const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const [post, setPost] = useState<BlogPost | null>(
    null
  );

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(
    null
  );

  const [useFallback, setUseFallback] =
    useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;

      setLoading(true);

      setError(null);

      try {
        // 🔥 STEP 1: Load cached blog instantly
        const cachedPost = sessionStorage.getItem(
          `blog_post_${slug}`
        );

        if (
          cachedPost &&
          cachedPost !== 'undefined'
        ) {
          const parsed = JSON.parse(cachedPost);

          setPost(parsed.post);

          setUseFallback(
            parsed.useFallback || false
          );

          setLoading(false);
        }

        // 🔥 STEP 2: Fetch latest post
        const gallery =
          await galleryService.getPublicGallery(
            'BLOG'
          );

        const item = gallery.items.find(
          (i: any) =>
            i.type === 'IMAGE' &&
            i.active &&
            (() => {
              try {
                const meta = JSON.parse(
                  i.metadata || '{}'
                );

                return (
                  meta.isBlogPost &&
                  meta.slug === slug
                );
              } catch {
                return false;
              }
            })()
        );

        let finalPost: BlogPost = staticPost;

        let fallbackState = false;

        if (item) {
          let meta: any = {};

          try {
            meta = JSON.parse(
              item.metadata || '{}'
            );
          } catch (e) {}

          finalPost = {
            title: meta.title || item.title,

            content: meta.content || '',

            image: item.imageUrl,

            author: meta.author || '',

            date: meta.date || '',

            category: meta.category || '',

            readTime: meta.readTime || '',

            tags: meta.tags || [],

            slug: meta.slug || slug,
          };

          fallbackState = false;
        } else {
          finalPost = staticPost;

          fallbackState = true;
        }

        setPost(finalPost);

        setUseFallback(fallbackState);

        // 🔥 STEP 3: Save cache
        sessionStorage.setItem(
          `blog_post_${slug}`,
          JSON.stringify({
            post: finalPost,
            useFallback: fallbackState,
          })
        );
      } catch (err) {
        console.error(
          'Failed to fetch blog post:',
          err
        );

        setPost(staticPost);

        setUseFallback(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const getImageUrl = (url: string) => {
    if (!url) return PLACEHOLDER_IMG;

    if (url.startsWith('http')) return url;

    if (url.startsWith('/assets')) return url;

    return `${API_URL}${
      url.startsWith('/') ? '' : '/'
    }${url}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';

    return new Date(
      dateString
    ).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,

        text: post?.content?.substring(0, 100),

        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(
        window.location.href
      );
    }
  };

  if (loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader />
      </Container>
    );
  }

  if (error && !post) {
    return (
      <Container>
        <Alert color="red" title="Error">
          {error}
        </Alert>

        <Button
          component={Link}
          to="/blog"
          mt="md"
          leftSection={
            <IconArrowLeft size={16} />
          }
        >
          Back to Blog
        </Button>
      </Container>
    );
  }

  const currentPost = post || staticPost;

  const featuredImage = getImageUrl(
    currentPost.image
  );

  return (
    <>
      <Helmet>
        <title>
          {currentPost.title} - Thalvora Exports
          Blog
        </title>

        <meta
          name="description"
          content={currentPost.content.substring(
            0,
            160
          )}
        />

        <meta
          property="og:title"
          content={currentPost.title}
        />

        <meta
          property="og:description"
          content={currentPost.content.substring(
            0,
            160
          )}
        />

        <meta
          property="og:image"
          content={featuredImage}
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />
      </Helmet>

      <Container size="md">
        <Button
          component={Link}
          to="/blog"
          variant="subtle"
          leftSection={
            <IconArrowLeft size={16} />
          }
          mb="xl"
        >
          Back to Blog
        </Button>

        <Stack gap="md" mb="xl">
          <Badge
            size="lg"
            color="primary"
            variant="light"
            style={{
              alignSelf: 'flex-start',
            }}
          >
            {currentPost.category}
          </Badge>

          <Title order={1} size="2.5rem">
            {currentPost.title}
          </Title>

          <Group gap="lg">
            <Group gap="xs">
              <IconUser size={16} />

              <Text size="sm">
                {currentPost.author}
              </Text>
            </Group>

            <Group gap="xs">
              <IconCalendar size={16} />

              <Text size="sm">
                {formatDate(
                  currentPost.date
                )}
              </Text>
            </Group>

            <Group gap="xs">
              <IconClock size={16} />

              <Text size="sm">
                {currentPost.readTime}
              </Text>
            </Group>
          </Group>
        </Stack>

        <Image
          src={featuredImage}
          alt={currentPost.title}
          radius="lg"
          mb="xl"
          style={{
            width: '100%',
            maxHeight: 500,
            objectFit: 'cover',
          }}
          fallbackSrc={PLACEHOLDER_IMG}
          loading="lazy"
        />

        <Paper
          withBorder
          p="xl"
          radius="lg"
          mb="xl"
        >
          <div
            dangerouslySetInnerHTML={{
              __html: currentPost.content,
            }}
            style={{
              fontSize: '1.1rem',
              lineHeight: 1.8,
            }}
          />
        </Paper>

        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Group gap="xs">
              {currentPost.tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="dot"
                  color="primary"
                >
                  #{tag}
                </Badge>
              ))}
            </Group>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Group justify="flex-end">
              <Button
                variant="light"
                leftSection={
                  <IconShare size={16} />
                }
                onClick={handleShare}
              >
                Share
              </Button>
            </Group>
          </Grid.Col>
        </Grid>
      </Container>
    </>
  );
};